/**
 * Extracts a conflict key for a Tailwind CSS class to detect duplicates.
 * Classes with modifiers (sm:, hover:, dark:) are kept separate from their base versions.
 *
 * @param className - The class name to analyze
 * @returns A key representing the conflict group (includes modifiers + base prefix)
 */
function getTailwindConflictKey(className: string): string {
  // Extract all modifier prefixes (responsive, state, dark mode)
  const modifierMatch = className.match(
    /^((?:sm|md|lg|xl|2xl|hover|focus|active|disabled|visited|checked|group-hover|group-focus|dark):)*/
  );
  const modifiers = modifierMatch ? modifierMatch[0] : '';
  const withoutModifiers = className.substring(modifiers.length);

  // Special handling for flex utilities BEFORE general Tailwind pattern check:
  // - 'flex' (display: flex) should not conflict with 'flex-1', 'flex-grow', etc.
  // - 'flex-1', 'flex-auto', 'flex-initial', 'flex-none' control flex grow/shrink
  // - 'flex-row', 'flex-col' control flex-direction
  // - 'flex-wrap', 'flex-nowrap' control flex-wrap
  if (withoutModifiers.startsWith('flex')) {
    // If it's just 'flex' (no suffix), use 'flex-display' as key
    if (withoutModifiers === 'flex') {
      return modifiers + 'flex-display';
    }
    // flex-1, flex-auto, etc. conflict with each other (flex grow/shrink)
    if (/^flex-(1|auto|initial|none)$/.test(withoutModifiers)) {
      return modifiers + 'flex-size';
    }
    // flex-row, flex-col, flex-row-reverse, flex-col-reverse conflict
    if (/^flex-(row|col)(-reverse)?$/.test(withoutModifiers)) {
      return modifiers + 'flex-direction';
    }
    // flex-wrap, flex-nowrap, flex-wrap-reverse conflict
    if (/^flex-(wrap|nowrap|wrap-reverse)$/.test(withoutModifiers)) {
      return modifiers + 'flex-wrap';
    }
    // flex-grow-*, flex-shrink-*, flex-basis-* have their own groups
    if (/^flex-grow/.test(withoutModifiers)) {
      return modifiers + 'flex-grow';
    }
    if (/^flex-shrink/.test(withoutModifiers)) {
      return modifiers + 'flex-shrink';
    }
  }

  // Special handling for 'text-' prefix utilities which control different CSS properties:
  // - text-(xs|sm|base|lg|xl|...) = font-size
  // - text-[arbitrary] = font-size (e.g., text-[12px])
  // - text-(left|center|right|...) = text-align
  // - text-(color names/numbers) = color
  // - text-opacity-* = text opacity
  if (withoutModifiers.startsWith('text-')) {
    // Font size utilities (including arbitrary values like text-[12px])
    if (
      /^text-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)$/.test(withoutModifiers) ||
      /^text-\[.+\]$/.test(withoutModifiers)
    ) {
      return modifiers + 'text-size';
    }
    // Text alignment utilities
    if (/^text-(left|center|right|justify|start|end)$/.test(withoutModifiers)) {
      return modifiers + 'text-align';
    }
    // Text opacity utilities
    if (/^text-opacity-/.test(withoutModifiers)) {
      return modifiers + 'text-opacity';
    }
    // Everything else with text- is color (text-white, text-red-500, text-white-a40, etc.)
    return modifiers + 'text-color';
  }

  // Special handling for 'bg-' prefix utilities:
  // - bg-(color names/numbers) = background-color
  // - bg-(top|bottom|center|...) = background-position
  // - bg-(cover|contain|auto|...) = background-size
  // - bg-opacity-* = background opacity
  if (withoutModifiers.startsWith('bg-')) {
    // Background position utilities
    if (/^bg-(top|bottom|center|left|right|left-top|left-bottom|right-top|right-bottom)$/.test(withoutModifiers)) {
      return modifiers + 'bg-position';
    }
    // Background size utilities
    if (/^bg-(cover|contain|auto)$/.test(withoutModifiers)) {
      return modifiers + 'bg-size';
    }
    // Background opacity utilities
    if (/^bg-opacity-/.test(withoutModifiers)) {
      return modifiers + 'bg-opacity';
    }
    // Everything else with bg- is color
    return modifiers + 'bg-color';
  }

  // Special handling for 'border-' prefix utilities:
  // - border, border-0, border-2, etc. = border-width
  // - border-(color names/numbers) = border-color
  // - border-(solid|dashed|dotted|...) = border-style
  // - border-opacity-* = border opacity
  if (withoutModifiers.startsWith('border')) {
    // Border width utilities (border, border-0, border-2, border-4, border-8)
    if (/^border(-\d+)?$/.test(withoutModifiers)) {
      return modifiers + 'border-width';
    }
    // Border style utilities
    if (/^border-(solid|dashed|dotted|double|hidden|none)$/.test(withoutModifiers)) {
      return modifiers + 'border-style';
    }
    // Border opacity utilities
    if (/^border-opacity-/.test(withoutModifiers)) {
      return modifiers + 'border-opacity';
    }
    // Side-specific border widths (border-t-2, border-r-4, etc.)
    if (/^border-[trbl][xy]?-\d+$/.test(withoutModifiers)) {
      const sideMatch = withoutModifiers.match(/^border-([trbl][xy]?)-/);
      const side = sideMatch ? sideMatch[1] : '';
      return modifiers + `border-${side}-width`;
    }
    // Everything else with border- is color
    return modifiers + 'border-color';
  }

  // Check if this looks like a Tailwind utility class
  // Must have format: prefix-value where value is number, size keyword, color, fraction, or arbitrary
  // Patterns: p-2, text-sm, bg-red-500, -m-2, w-1/2, w-full, bg-[#fff], etc.
  const tailwindPattern =
    /^-?[a-z]+-(\d+|xs|sm|md|lg|xl|2xl|3xl|base|auto|full|screen|none|\d+\/\d+|\[.+\]|[a-z]+-\d+)/;
  const isTailwindUtility = tailwindPattern.test(withoutModifiers);

  if (!isTailwindUtility) {
    // Not a Tailwind utility - return full className to prevent false conflicts
    // This ensures 'btn' and 'btn-primary' are kept as separate classes
    return className;
  }

  // Extract the base prefix (e.g., 'p' from 'p-2', 'text' from 'text-sm')
  const prefixMatch = withoutModifiers.match(/^-?([a-z]+)-/);
  const prefix = prefixMatch ? prefixMatch[1] : withoutModifiers;

  // Return modifiers + prefix as the conflict key
  // This ensures 'p-2' and 'p-4' conflict, but 'sm:p-2' and 'p-4' don't
  return modifiers + prefix;
}

/**
 * Combines multiple class names into a single string with Tailwind CSS conflict resolution.
 * Filters out any falsy values and ensures the first occurrence of a Tailwind class type wins.
 *
 * When multiple classes with the same Tailwind prefix are provided (e.g., 'p-2' and 'p-0'),
 * only the first one is kept to avoid conflicts. Classes with modifiers are treated separately.
 *
 * Example usage:
 *    classNames('p-2', 'p-0'); // Output: 'p-2' (first padding wins)
 *    classNames('text-sm', 'text-lg', 'font-bold'); // Output: 'text-sm font-bold'
 *    classNames('btn', 'btn-primary', isActive && 'active'); // Output: 'btn btn-primary active'
 *    classNames('p-2', 'sm:p-4'); // Output: 'p-2 sm:p-4' (different modifiers, both kept)
 *
 * @param {...unknown[]} classes - An array of class names which can be of any type.
 * @returns {string} A single string containing all the valid class names separated by a space.
 */
export function classNames(...classes: unknown[]): string {
  // Filter out falsy values and split space-separated classes
  const allClasses = classes
    .filter(Boolean)
    .flatMap((cls) => String(cls).split(' '))
    .filter(Boolean);

  // Track which Tailwind conflict keys we've seen
  const seenKeys = new Set<string>();
  const result: string[] = [];

  for (const className of allClasses) {
    const conflictKey = getTailwindConflictKey(className);

    // If we haven't seen this conflict key yet, add the class
    if (!seenKeys.has(conflictKey)) {
      seenKeys.add(conflictKey);
      result.push(className);
    }
  }

  return result.join(' ');
}
